// src/components/home/Hero.tsx
import React, { useEffect, useRef, useState } from 'react';
import { getPrefectures } from '../../data/prefectures';
import { getTotalCourseCount } from '../../data/courses';
import { getPolicePositions } from '../../data/policePositions';
import { useConfigValue } from '../../config/gameConfig';

interface HeroProps {
    onStart: () => void;
    onLogin: () => void;
}

const prefersReducedMotion = () =>
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Counts up to `target` on mount.
 *
 * Uses requestAnimationFrame rather than a 16 ms interval so the browser paces the
 * work, and settles exactly on the target instead of overshooting.
 */
const useCountUp = (target: number, durationMs = 1100): number => {
    const [value, setValue] = useState(() => (prefersReducedMotion() ? target : 0));

    useEffect(() => {
        if (prefersReducedMotion() || target <= 0) {
            setValue(target);
            return;
        }

        let frame = 0;
        let start: number | null = null;

        const step = (now: number) => {
            if (start === null) start = now;
            const progress = Math.min((now - start) / durationMs, 1);
            // Ease-out so the last few digits settle rather than snapping.
            const eased = 1 - Math.pow(1 - progress, 3);
            setValue(Math.round(target * eased));
            if (progress < 1) frame = requestAnimationFrame(step);
        };

        frame = requestAnimationFrame(step);
        return () => cancelAnimationFrame(frame);
    }, [target, durationMs]);

    return value;
};

export const Hero: React.FC<HeroProps> = ({ onStart, onLogin }) => {
    const heroRef = useRef<HTMLElement>(null);

    // Read from the Firestore-backed game configuration, which the app loads before
    // rendering, so these never drift from what is actually in the game.
    const stats = useConfigValue(() => {
        const departments = getPrefectures().reduce((sum, p) => sum + p.departments.length, 0);
        return {
            departments,
            courses: getTotalCourseCount(),
            positions: getPolicePositions().length
        };
    }, []);

    const departments = useCountUp(stats.departments);
    const courses = useCountUp(stats.courses);
    const positions = useCountUp(stats.positions);

    return (
        <section className="lp-hero" ref={heroRef}>
            <div className="lp-hero__bg" aria-hidden="true">
                <div className="lp-hero__grid" />
                <div className="lp-hero__glow" />
                <div className="lp-hero__beam" />
                <div className="lp-hero__grain" />
            </div>

            <div className="lp-hero__inner">
                <div className="lp-hero__copy">
                    <p className="lp-eyebrow lp-enter lp-enter--1">
                        <span className="lp-eyebrow__dot" />
                        Eesti politsei rollimäng
                    </p>

                    <h1 className="lp-hero__title lp-enter lp-enter--2">
                        Võmm<span className="lp-hero__title-mark">.</span>
                    </h1>

                    <div className="lp-hero__rule lp-enter lp-enter--3">
                        <span className="lp-hero__kicker">Taasta kord Eesti linnades</span>
                    </div>

                    <p className="lp-lede lp-enter lp-enter--4">
                        Alusta politseikoolist, tõuse läbi auastmete ja ehita koos kolleegidega
                        üles piirkonna tugevaim jaoskond. Iga vahetus, iga treening ja iga otsus
                        viib sind astme võrra edasi.
                    </p>

                    <div className="lp-hero__actions lp-enter lp-enter--5">
                        <button type="button" className="lp-btn lp-btn--signal" onClick={onStart}>
                            Alusta karjääri
                        </button>
                        <button type="button" className="lp-btn lp-btn--ghost" onClick={onLogin}>
                            Logi sisse
                        </button>
                    </div>
                </div>

                <div className="lp-hero__art">
                    <img
                        src="/images/mainPageHero-New.png"
                        alt="Kolm Võmmi mängu politseinikku teenistuses"
                        className="lp-hero__img lp-art-enter"
                        width={1280}
                        height={720}
                        /* Above the fold: fetch it eagerly and at high priority so the
                           key art is not the last thing to arrive. */
                        loading="eager"
                        fetchPriority="high"
                        decoding="async"
                    />
                </div>
            </div>

            <div className="lp-hero__strip">
                <div className="lp-stat">
                    <span className="lp-stat__value">{departments}</span>
                    <span className="lp-stat__label">Jaoskonda üle Eesti</span>
                </div>
                <div className="lp-stat">
                    <span className="lp-stat__value">
                        {courses}
                        <em>+</em>
                    </span>
                    <span className="lp-stat__label">Koolitust ja eksamit</span>
                </div>
                <div className="lp-stat">
                    <span className="lp-stat__value">{positions}</span>
                    <span className="lp-stat__label">Ametikohta valida</span>
                </div>
            </div>
        </section>
    );
};
